import React from 'react';

interface WellVisualizationProps {
  levelPercent: number; // 0-100
  thresholdPercent?: number; // 0-100, optional threshold line
  currentLiters?: number; // Current volume in liters
  thresholdLiters?: number; // Threshold volume in liters
  maxCapacityLiters?: number; // Maximum capacity in liters (calculated from well dimensions)
  height?: number; // Height in pixels, default 400
  width?: number; // Width in pixels, default 250
  className?: string;
  wellDepth?: number; // Well depth in meters, default 10
}

export default function WellVisualization({
  levelPercent,
  thresholdPercent,
  currentLiters,
  thresholdLiters,
  maxCapacityLiters,
  height = 400,
  width = 250,
  className = '',
  wellDepth = 10,
}: WellVisualizationProps) {
  // Clamp values between 0 and 100
  const clampedLevel = Math.max(0, Math.min(100, levelPercent));
  const clampedThreshold = thresholdPercent
    ? Math.max(0, Math.min(100, thresholdPercent))
    : undefined;

  // Calculate dimensions for the well
  const wellMargin = 30;
  const wellWidth = width - wellMargin * 2;
  const wellHeight = height - 80; // Space for ground level and labels
  const groundLevel = 40;
  const wellTop = groundLevel + 20;
  const wellLeft = wellMargin;

  // Calculate water level (from bottom of well)
  const waterHeight = (clampedLevel / 100) * wellHeight;
  const thresholdY = clampedThreshold
    ? wellTop + wellHeight - (clampedThreshold / 100) * wellHeight
    : undefined;

  // Well casing dimensions
  const casingThickness = 8;
  const wellCapSize = 20;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="relative" style={{ width, height }}>
        <svg width={width} height={height} className="overflow-visible">
          {/* Ground surface */}
          <rect
            x="0"
            y={groundLevel}
            width={width}
            height="20"
            fill="#8B7355"
            className="ground-surface"
          />

          {/* Grass/surface texture */}
          <rect x="0" y={groundLevel} width={width} height="4" fill="#22C55E" className="grass" />

          {/* Well casing (outer) */}
          <rect
            x={wellLeft - casingThickness}
            y={wellTop}
            width={wellWidth + casingThickness * 2}
            height={wellHeight}
            fill="#6B7280"
            stroke="#374151"
            strokeWidth="2"
            className="well-casing"
          />

          {/* Well interior */}
          <rect
            x={wellLeft}
            y={wellTop}
            width={wellWidth}
            height={wellHeight}
            fill="#1E293B"
            className="well-interior"
          />

          {/* Water fill */}
          {waterHeight > 0 && (
            <rect
              x={wellLeft + 2}
              y={wellTop + wellHeight - waterHeight}
              width={wellWidth - 4}
              height={waterHeight}
              fill="#0EA5E9"
              fillOpacity="0.8"
              className="water-fill"
            />
          )}

          {/* Water surface with ripple effect */}
          {waterHeight > 0 && (
            <>
              <defs>
                <radialGradient id="waterSurface" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#0284C7" stopOpacity="0.9" />
                  <stop offset="70%" stopColor="#0EA5E9" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#0284C7" stopOpacity="0.9" />
                </radialGradient>
              </defs>
              <ellipse
                cx={wellLeft + wellWidth / 2}
                cy={wellTop + wellHeight - waterHeight}
                rx={(wellWidth - 4) / 2}
                ry="3"
                fill="url(#waterSurface)"
              />
            </>
          )}

          {/* Threshold line */}
          {thresholdY !== undefined && (
            <line
              x1={wellLeft - 10}
              y1={thresholdY}
              x2={wellLeft + wellWidth + 10}
              y2={thresholdY}
              stroke="#EF4444"
              strokeWidth="2"
              strokeDasharray="5,5"
              className="threshold-line"
            />
          )}

          {/* Well cap/cover */}
          <rect
            x={wellLeft - wellCapSize}
            y={wellTop - wellCapSize / 2}
            width={wellWidth + wellCapSize * 2}
            height={wellCapSize}
            fill="#374151"
            stroke="#1F2937"
            strokeWidth="2"
            rx="4"
            className="well-cap"
          />

          {/* Well cap bolts */}
          <circle cx={wellLeft - wellCapSize / 2} cy={wellTop} r="2" fill="#6B7280" />
          <circle cx={wellLeft + wellWidth + wellCapSize / 2} cy={wellTop} r="2" fill="#6B7280" />

          {/* Measurement rope/cable */}
          <line
            x1={wellLeft + wellWidth / 2}
            y1={wellTop}
            x2={wellLeft + wellWidth / 2}
            y2={wellTop + wellHeight - waterHeight + 5}
            stroke="#94A3B8"
            strokeWidth="1"
            strokeDasharray="2,2"
            className="measurement-cable"
          />

          {/* Sensor probe */}
          {waterHeight > 0 && (
            <rect
              x={wellLeft + wellWidth / 2 - 3}
              y={wellTop + wellHeight - waterHeight + 2}
              width="6"
              height="8"
              fill="#374151"
              rx="1"
              className="sensor-probe"
            />
          )}

          {/* Depth markers */}
          {[0.25, 0.5, 0.75, 1.0].map((fraction, index) => {
            const markerY = wellTop + fraction * wellHeight;
            const depth = (wellDepth * fraction).toFixed(1);
            return (
              <g key={index}>
                <line
                  x1={wellLeft - 5}
                  y1={markerY}
                  x2={wellLeft}
                  y2={markerY}
                  stroke="#6B7280"
                  strokeWidth="1"
                />
                <text
                  x={wellLeft - 8}
                  y={markerY + 3}
                  fontSize="10"
                  fill="#6B7280"
                  textAnchor="end"
                  className="depth-marker"
                >
                  {depth}m
                </text>
              </g>
            );
          })}
        </svg>

        {/* Percentage labels */}
        <div
          className="absolute -right-20 flex flex-col justify-between text-xs text-gray-500"
          style={{ top: `${wellTop}px`, height: `${wellHeight}px` }}
        >
          <div className="text-right">
            <div>100%</div>
            {maxCapacityLiters && (
              <div className="text-gray-400">{maxCapacityLiters.toFixed(0)}L</div>
            )}
          </div>
          <div className="text-right">
            <div>75%</div>
            {maxCapacityLiters && (
              <div className="text-gray-400">{Math.round(maxCapacityLiters * 0.75)}L</div>
            )}
          </div>
          <div className="text-right">
            <div>50%</div>
            {maxCapacityLiters && (
              <div className="text-gray-400">{Math.round(maxCapacityLiters * 0.5)}L</div>
            )}
          </div>
          <div className="text-right">
            <div>25%</div>
            {maxCapacityLiters && (
              <div className="text-gray-400">{Math.round(maxCapacityLiters * 0.25)}L</div>
            )}
          </div>
          <div className="text-right">
            <div>0%</div>
            <div className="text-gray-400">0L</div>
          </div>
        </div>

        {/* Current level indicator */}
        <div
          className="absolute -left-20 text-sm font-semibold text-blue-600"
          style={{ top: `${wellTop + wellHeight - waterHeight - 10}px` }}
        >
          <div className="text-right">
            <div>{clampedLevel.toFixed(0)}%</div>
            {currentLiters !== undefined && (
              <div className="text-xs font-normal">{currentLiters.toFixed(0)}L</div>
            )}
          </div>
        </div>

        {/* Threshold indicator */}
        {thresholdY !== undefined && (
          <div
            className="absolute -right-32 text-xs font-medium text-red-500"
            style={{ top: `${thresholdY - 8}px` }}
          >
            <div className="text-right">
              <div>Threshold: {clampedThreshold}%</div>
              {thresholdLiters !== undefined && (
                <div className="text-red-400">{thresholdLiters.toFixed(0)}L</div>
              )}
            </div>
          </div>
        )}

        {/* Well depth indicator */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-8 text-xs text-gray-600 font-medium text-center">
          <div>
            Depth: {wellDepth}m | Water Level: {((clampedLevel / 100) * wellDepth).toFixed(1)}m
          </div>
          {currentLiters !== undefined && maxCapacityLiters && (
            <div className="text-gray-400 mt-1">
              {currentLiters.toFixed(0)}L / {maxCapacityLiters.toFixed(0)}L (
              {clampedLevel.toFixed(1)}%)
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center space-x-4 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-sky-500 bg-opacity-80 rounded"></div>
          <span>Water Level</span>
        </div>
        {thresholdPercent !== undefined && (
          <div className="flex items-center space-x-1">
            <div className="w-3 h-1 bg-red-500" style={{ borderTop: '2px dashed #EF4444' }}></div>
            <span>Threshold</span>
          </div>
        )}
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 border-2 border-gray-500 bg-gray-700 rounded-sm"></div>
          <span>Well Casing</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
          <span>Ground Level</span>
        </div>
      </div>
    </div>
  );
}
